import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { toast } from 'sonner';

import { layersAtom, type LayerData } from '@/atoms/layers.atom';
import { DEFAULT_STOCKPILE_TOOL_MODE, stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import { pointCloudTargetsAtom } from '@/atoms/viewer.atom';
import { execute } from '@/graphql/execute';
import type { ProcessJobType } from '@/graphql/graphql';
import { useFeatureCheck } from '@/hooks/useFeature';
import { loadFeaturesByLayerOfflineAware } from '@/lib/offline/offlineFeatures';
import { convexHullXY, extractOuterBoundary } from '@/lib/stockpile/pointInPolygon';
import { useDialog } from '@/providers/DialogProvider';
import { getProjectQuery } from '@/queries/project.query';

import { isClearanceCandidate } from './candidates';
import {
  openClearance,
  openCrestLine,
  openCompareSurfaces,
  openGroundClassify,
  openIprjExport,
  openLandxmlExport,
  openLandxmlMerge,
  openMesh,
  openRainfall,
  type OpenerEnv,
} from './openAnalysisDialogs';
import { analysisGate } from './registry';
import type { AnalysisId } from './types';
import { useOpenAnalysisRerun } from './useOpenAnalysisRerun';

export interface LaunchOptions {
  contextLayer?: LayerData;
  /** P5: re-run this past analysis — its input is fetched lazily and mapped
   *  onto the dialog's initial props (`useOpenAnalysisRerun`). */
  rerunJobId?: string;
  /** The re-run's job type (the hub row's `run.type`): which engine a
   *  Compare surfaces run came from, for `buildRerunPlan`. */
  rerunJobType?: ProcessJobType;
}

/**
 * Gate check (locked → promptUpgrade, return — the gate comes from
 * `analysisGate`, so stockpile's draw mode checks the free tool), then opens
 * the analysis's dialog/panel through `openAnalysisDialogs` (component, props,
 * className and DialogOptions pinned by useLaunchAnalysis.dom.test and
 * shellOpeners.test).
 *
 * Without a context layer (the launcher): compare surfaces,
 * ground classification, mesh, rainfall and clearance open on their own
 * pickers and stockpile starts the draw tool; crest line and LandXML export
 * are a no-op (the launcher always sends their layer).
 *
 * With `rerunJobId` (P5): the past run's input is resolved first
 * (`useOpenAnalysisRerun`, which checks availability before fetching); it
 * either opens the dialog itself or hands launch options back to this path.
 */
export function useLaunchAnalysis(): (id: AnalysisId, opts?: LaunchOptions) => void {
  const { t } = useTranslation('project');
  // Dialogs render above <Routes> (DialogProvider), where useParams() is {}.
  const projectId = useMatch('/projects/:projectId/*')?.params.projectId;
  const dialog = useDialog();
  const featureCheck = useFeatureCheck();
  const allLayers = useAtomValue(layersAtom);
  // Use the live PCO atom rather than filtering layers by `isHidden` —
  // `pointCloudTargetsAtom` is populated by `<PointCloud>` only with PCOs
  // that are *actually loaded into the scene*. The layer's `isHidden` field
  // is server-side soft-delete state, NOT the user-toggled eye icon: a
  // child PCO turned off via the eye toggle stays `isHidden=false` on the
  // record (it's removed from `selectedLayerIds` instead).
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const setStockpileToolMode = useSetAtom(stockpileToolModeAtom);
  const openRerun = useOpenAnalysisRerun();
  // CrestLineDialog needs the project's SRID to tag/reproject the geometry
  // it saves. Same queryKey as useLayers' project fetch, so this dedupes
  // against the cache rather than issuing a second request per row.
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId! }),
    enabled: !!projectId,
  });

  /**
   * "Calculate stockpile volume" on a VECTOR layer. Useful when the user
   * already has the pile outline as one or more polygon features — including
   * TINs exported as a fan of triangle polygons. We collect every polygon's
   * outer ring, extract the outer boundary (convex hull only as a fallback),
   * auto-pick the only visible point cloud as the sampling source, and seed
   * the stockpile tool atom — the compute panel opens immediately.
   */
  const seedStockpileFromLayer = async (layer: LayerData, pid: string) => {
    if (layer.type !== 'VECTOR') return;
    const featuresData = await loadFeaturesByLayerOfflineAware([layer.id], pid);
    const polygonFeatures = featuresData.filter((f) => {
      const geom = f.geometry as { type?: string } | null;
      return geom?.type === 'Polygon' || geom?.type === 'MultiPolygon';
    });
    if (polygonFeatures.length === 0) {
      toast.error(t('viewerLayers.stockpileLayerNoPolygons'));
      return;
    }

    // Collect every polygon's outer ring as a separate ring entry. The
    // boundary extractor needs them as ring lists, not a flat vertex pile —
    // edge topology matters. Inner rings (holes) are discarded; they don't
    // belong to the outer boundary.
    const allRings: Array<Array<[number, number, number]>> = [];
    const allVerts: Array<[number, number, number]> = [];
    for (const f of polygonFeatures) {
      const geom = f.geometry as
        | { type: 'Polygon'; coordinates: number[][][] }
        | { type: 'MultiPolygon'; coordinates: number[][][][] }
        | null;
      if (!geom) continue;
      const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;
      for (const poly of polys) {
        const outerRing = poly?.[0];
        if (!outerRing) continue;
        const ring: Array<[number, number, number]> = [];
        for (const v of outerRing) {
          const tup: [number, number, number] = [v[0], v[1], v[2] ?? 0];
          ring.push(tup);
          allVerts.push(tup);
        }
        allRings.push(ring);
      }
    }
    if (allRings.length === 0) {
      toast.error(t('viewerLayers.stockpileLayerNoPolygons'));
      return;
    }

    // Edge-based boundary extraction. For TINs (many triangle polygons sharing
    // edges), this produces the *exact* concave outer boundary along the
    // triangulation perimeter — no over-inclusion. For single polygons or
    // disjoint multi-polygons, see the docs on `extractOuterBoundary`.
    let polygonRing = extractOuterBoundary(allRings);
    if (!polygonRing || polygonRing.length < 3) {
      // Fall back to convex hull only when topology fails (no closed boundary
      // ring could be built). Better an over-inclusive hull than an error.
      polygonRing = convexHullXY(allVerts);
    }
    if (polygonRing.length < 3) {
      toast.error(t('viewerLayers.stockpileLayerNoPolygons'));
      return;
    }

    // Auto-pick visible PCO from the live atom — only PCOs actually loaded
    // into the scene appear here, so a hidden child or an unloaded layer
    // is correctly excluded.
    const visiblePcoLayerIds = pcoTargets
      .map((p) => p.userData?.layerId as string | undefined)
      .filter((id): id is string => !!id);
    if (visiblePcoLayerIds.length === 0) {
      toast.error(t('viewerLayers.stockpileNoVisiblePco'));
      return;
    }
    setStockpileToolMode({
      enabled: true,
      phase: 'closed',
      pickedPoints: polygonRing,
      sourceLayerId: visiblePcoLayerIds[0],
      editFeatureId: null,
    });
  };

  const launchNow = (id: AnalysisId, opts: LaunchOptions = {}): void => {
    const gateKey = analysisGate(id, opts.contextLayer);
    const gate = gateKey === null ? { allowed: true, promptUpgrade: () => {} } : featureCheck(gateKey);
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    if (!projectId) return;
    const env: OpenerEnv = {
      openDialog: dialog.openDialog,
      projectId,
      layers: allLayers,
    };
    const layer = opts.contextLayer;

    switch (id) {
      case 'compare':
        // The dialog has its own pickers, so no context is fine.
        openCompareSurfaces(env, layer);
        return;
      case 'clearance': {
        const source = layer ?? allLayers.find(isClearanceCandidate);
        if (source) openClearance(env, source);
        return;
      }
      case 'groundClassify':
        openGroundClassify(env, layer);
        return;
      case 'mesh':
        openMesh(env, layer);
        return;
      case 'rainfall':
        // The worker validates triangle shape on read and fails fast with a
        // clear error if the layer is not a TIN, so the menu does not
        // pre-filter by feature shape.
        openRainfall(env, layer);
        return;
      case 'landxmlExport':
        if (layer) openLandxmlExport(env, layer);
        return;
      case 'landxmlMerge':
        openLandxmlMerge(env, layer);
        return;
      case 'iprjExport':
        openIprjExport(env, layer);
        return;
      case 'crestLine': {
        const srid = projectData?.project?.srid;
        if (layer && srid != null) openCrestLine(env, layer.id, srid);
        return;
      }
      case 'stockpile':
        if (!layer) {
          // Same as the viewer toolbar's stockpile button: enter draw mode.
          setStockpileToolMode({ ...DEFAULT_STOCKPILE_TOOL_MODE, enabled: true });
          return;
        }
        void seedStockpileFromLayer(layer, projectId);
        return;
      default: {
        const unreachable: never = id;
        return unreachable;
      }
    }
  };

  return (id, opts) => {
    if (opts?.rerunJobId) {
      const jobId = opts.rerunJobId;
      // Gate checks happen inside: `openRerun` checks availability before it
      // fetches anything, and the fallback goes through `launchNow`'s own.
      openRerun(id, jobId, opts.rerunJobType)
        .then((fallback) => {
          if (fallback) launchNow(id, fallback);
        })
        .catch(() => toast.error(t('analysisLauncher.runs.inputFailed')));
      return;
    }
    launchNow(id, opts);
  };
}
